import { FeatureSwitch, FeatureSwitchInfo } from '@if/core/types'
import { FinancingConfigDto } from '@src/data/types/FinancingConfigDto'
import { ProductsProvider } from '@src/data/types/ProductsProvider'
import { ProvinceConfigs } from '@src/data/types/ProvinceConfigs'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Territory, ValueListItem } from '../../types'
import { DocumentType } from '../../types/DocumentType'
import { TaskType } from '../../types/TaskType'
import { TranslatedEnum } from '../../types/TranslatedEnum'
import { DocumentTypeDto } from './DocumentTypeDto'
import { TaskTypeDto } from './TaskTypeDto'
import { TranslatedEnumDto } from './TranslatedEnumDto'
import {
  transformDocumentTypeFromApi,
  transformTaskTypeFromApi,
  transformTranslatedEnumFromApi,
} from './config-transform'

export interface ValueListItemDto {
  value: string
  textFr: string
  textEn: string
  priority?: number
  active: boolean
}

export interface ValueListDto {
  id: string
  sortMethod: 'priority' | 'alpha'
  defaultvalue: string | null
  items: ValueListItemDto[]
  expiresOn: number
}

/* Section API React query hooks */

const SCOPE = 'config'
const SCOPE_TERRITORIES = 'territories'
const SCOPE_ENUMS = 'enums'
const SCOPE_DOCUMENT_TYPES = 'document-types'
const SCOPE_FINANCING_CONFIGS = 'financing-configs'
const SCOPE_PROVINCE_CONFIG = 'province-config'
const SCOPE_TASK_TYPES = 'task-types'
const SCOPE_HOLIDAYS = 'holidays'
const SCOPE_PRODUCT_PROVIDERS = 'product-providers'
const SCOPE_FEATURE_SWITCHES = 'feature-switches'
const LIST = 'list'

const keysFactory = {
  list: (listId: string) => [SCOPE, LIST, listId] as const,
  territories: () => [SCOPE_TERRITORIES] as const,
  enums: () => [SCOPE_ENUMS] as const,
  documentTypes: () => [SCOPE_DOCUMENT_TYPES] as const,
  financingConfigs: () => [SCOPE_FINANCING_CONFIGS] as const,
  provinceConfig: () => [SCOPE_PROVINCE_CONFIG] as const,
  taskTypes: () => [SCOPE_TASK_TYPES] as const,
  holidays: (year: number) => [SCOPE_HOLIDAYS, year] as const,
  productProviders: () => [SCOPE_PRODUCT_PROVIDERS] as const,
  featureSwitches: () => [SCOPE_FEATURE_SWITCHES] as const,
}

function buildValueList(list: ValueListDto | null, lang: string) {
  if (!list) return []

  const inputList: ValueListItemDto[] = [...list.items]
  if (list.sortMethod === 'priority') inputList.sort((x) => x.priority ?? 0)

  const items: ValueListItem[] = inputList.map((x) => {
    return {
      id: x.value,
      text: lang === 'fr' ? x.textFr : x.textEn,
    }
  })
  if (list.sortMethod === 'alpha') items.sort((one, two) => (one.text > two.text ? -1 : 1))
  return items
}

// Query functions
const getValueList = async (configId: string) => {
  const response = await getApiClient().get<ValueListDto>(`/Configs/${configId}`)
  return response.data
}

const getTerritories = async () => {
  const response = await getApiClient().get<Territory[]>('/Territories')
  return response.data
}

const getEnums = async () => {
  const response = await getApiClient().get<Record<string, Record<string, TranslatedEnumDto>>>('/Enums')
  return transformTranslatedEnumFromApi(response.data)
}

const getDocumentTypes = async () => {
  const response = await getApiClient().get<Record<string, DocumentTypeDto>>('/DocumentType')
  return transformDocumentTypeFromApi(response.data)
}

const getFinancingConfigs = async () => {
  const response = await getApiClient().get<FinancingConfigDto[]>('/FinancingConfigs')
  return response.data
}

const getProvinceConfigurations = async () => {
  const response = await getApiClient().get<ProvinceConfigs>('/ProvinceConfig')
  return response.data
}

const getTaskTypes = async () => {
  const response = await getApiClient().get<Record<string, TaskTypeDto>>('/TaskType')
  return transformTaskTypeFromApi(response.data)
}

const getIFinanceHolidays = async (year: number) => {
  const response = await getApiClient().get<Date[]>(`/FinancingCompanyEnum/Companies/iFinance/Holidays/${year}`)
  return response.data
}

const getProductProviders = async () => {
  const response = await getApiClient().get<ProductsProvider[]>('/ProductProvider')
  return response.data
}

const getAllFeatureSwitches = async () => {
  const response = await getApiClient().get<FeatureSwitchInfo>('/FeatureSwitch')
  return response.data
}

const updateFeatureSwitch = async (id: string, data: FeatureSwitch) => {
  const response = await getApiClient().put<FeatureSwitch>(`/FeatureSwitch/${id}`, data)
  return response.data
}

// Hooks
export function useGetValueListRaw(configId: string): [ValueListDto | null, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.list(configId),
    queryFn: () => getValueList(configId),
  })

  return [data ?? null, isFetching]
}

export function useGetValueList(configId: string): [ValueListItem[], boolean] {
  const {
    i18n: { resolvedLanguage },
  } = useTranslation()

  const { data, isFetching } = useQuery({
    queryKey: keysFactory.list(configId),
    queryFn: () => getValueList(configId),
  })

  const formattedData = useMemo(() => buildValueList(data ?? null, resolvedLanguage ?? 'en'), [data, resolvedLanguage])

  return [formattedData, isFetching]
}

export function useGetTerritories(): [Territory[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.territories(),
    queryFn: getTerritories,
  })

  return [data ?? [], isFetching]
}

export function useGetEnums(): [Record<string, Record<string, TranslatedEnum>>, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.enums(),
    queryFn: getEnums,
  })

  return [data ?? {}, isFetching]
}

export function useGetDocumentTypes(): [Record<number, DocumentType>, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.documentTypes(),
    queryFn: getDocumentTypes,
  })

  return [data ?? {}, isFetching]
}

export function useGetFinancingConfigs(): [FinancingConfigDto[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.financingConfigs(),
    queryFn: getFinancingConfigs,
  })

  return [data ?? [], isFetching]
}

export function useGetProvinceConfigurations(): [ProvinceConfigs, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.provinceConfig(),
    queryFn: getProvinceConfigurations,
  })

  return [data ?? {}, isFetching]
}

export function useGetTaskTypes(): [Record<number, TaskType>, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.taskTypes(),
    queryFn: getTaskTypes,
  })

  return [data ?? {}, isFetching]
}

export function useGetIFinanceHolidays(year: number): [Date[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.holidays(year),
    queryFn: () => getIFinanceHolidays(year),
    enabled: !!year,
  })

  return [data ?? [], isFetching]
}

export function useGetProductProviders(): [ProductsProvider[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.productProviders(),
    queryFn: getProductProviders,
  })

  return [data ?? [], isFetching]
}

export function useGetAllFeatureSwitches(): [FeatureSwitchInfo | undefined, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.featureSwitches(),
    queryFn: getAllFeatureSwitches,
  })

  return [data, isFetching]
}

/**
 * Hook to update a feature switch
 * Updates the cache directly on success
 */
export function useUpdateFeatureSwitch(): [MutationFunction<FeatureSwitch, FeatureSwitch>, boolean] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: FeatureSwitch) => updateFeatureSwitch(data.id, data),
    onSuccess: (updatedSwitch) => {
      const queryKey = keysFactory.featureSwitches()
      const currentData = queryClient.getQueryData<FeatureSwitchInfo>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FeatureSwitchInfo>(queryKey, {
          ...currentData,
          [updatedSwitch.id]: updatedSwitch,
        })
      }
    },
  })
  return [mutateAsync, isPending]
}

// Export query functions for direct use
export {
  getAllFeatureSwitches,
  getDocumentTypes,
  getEnums,
  getFinancingConfigs,
  getIFinanceHolidays,
  getProductProviders,
  getProvinceConfigurations,
  getTaskTypes,
  getTerritories,
  getValueList,
  updateFeatureSwitch,
}
